import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';
import './Management.css';

const StickerSizeManagement = () => {
  const { token } = useAuth();
  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    dimensions: '',
    price: '',
    order: '',
  });

  useEffect(() => {
    fetchSizes();
  }, [token]);

  const fetchSizes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/sticker-sizes?sort=order:asc`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Kan formaten niet laden');
      const data = await response.json();
      setSizes(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ label: '', dimensions: '', price: '', order: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const method = editingId ? 'PUT' : 'POST';
      const endpoint = editingId
        ? `${API_URL}/api/sticker-sizes/${editingId}`
        : `${API_URL}/api/sticker-sizes`;

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          data: {
            label: formData.label,
            dimensions: formData.dimensions,
            price: parseFloat(formData.price) || 0,
            order: parseInt(formData.order, 10) || 0,
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Kan formaat niet opslaan');
      }
      await fetchSizes();
      resetForm();
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Weet je zeker dat je dit formaat wilt verwijderen?')) return;

    try {
      const response = await fetch(`${API_URL}/api/sticker-sizes/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Kan formaat niet verwijderen');
      await fetchSizes();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (size) => {
    const attrs = size.attributes || size;
    setFormData({
      label: attrs.label || '',
      dimensions: attrs.dimensions || '',
      price: attrs.price ?? '',
      order: attrs.order ?? '',
    });
    setEditingId(size.id);
    setShowForm(true);
  };

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Sticker Formaten</h1>
        <button className="btn-primary" onClick={() => showForm ? resetForm() : setShowForm(true)}>
          {showForm ? 'Annuleren' : '+ Formaat Toevoegen'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <form className="admin-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Label (bv. S, M, L, XL)</label>
            <input
              type="text"
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Afmetingen (bv. 10×10 cm)</label>
            <input
              type="text"
              value={formData.dimensions}
              onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })}
              required
            />
          </div>
          <div className="form-row-split">
            <div className="form-group">
              <label>Prijs (€)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Volgorde</label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                required
              />
            </div>
          </div>
          <button type="submit" className="btn-submit">
            {editingId ? 'Formaat Bijwerken' : 'Formaat Aanmaken'}
          </button>
        </form>
      )}

      {loading ? (
        <p className="loading">Formaten worden geladen...</p>
      ) : sizes.length === 0 ? (
        <p className="empty">Geen formaten gevonden</p>
      ) : (
        <div className="items-table">
          <table>
            <thead>
              <tr>
                <th>Label</th>
                <th>Afmetingen</th>
                <th>Prijs</th>
                <th>Volgorde</th>
                <th>Acties</th>
              </tr>
            </thead>
            <tbody>
              {sizes.map((size) => {
                const attrs = size.attributes || size;
                return (
                  <tr key={size.id}>
                    <td>{attrs.label}</td>
                    <td>{attrs.dimensions}</td>
                    <td>€{Number(attrs.price).toFixed(2)}</td>
                    <td>{attrs.order}</td>
                    <td className="actions">
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(size)}
                      >
                        Bewerken
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(size.id)}
                      >
                        Verwijderen
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StickerSizeManagement;
